<template>
  <div class="survey-filter">
    <div class="row border-bottom white-bg dashboard-header">
      <div class="col-lg-3">
        <h1>Survey Filter</h1>
      </div>
    </div>
    <div class="wrapper wrapper-content animated fadeInRight">
      <i-box>
        <div slot="title">
          <div class="row">
            <div class="col-xs-6">
              <h2>Filter Builder</h2>
            </div>
          </div>
        </div>
        <div slot="content">
          <el-form label-width="200px" label-position="left">
            <div class="row">
              <div class="col-md-12" v-if="$apollo.loading">
                <loader message="Loading surveys..." />
              </div>
              <div class="col-md-12" v-if="surveys.length > 0">
                <h3>Survey</h3>
                  <el-select
                    v-model="filterData.survey_id"
                    placeholder="Please select a survey"
                    clearable
                     v-loading="loadingSurvey"
                    @change="handleSurveyChanged"
                  >
                    <el-option
                      v-for="survey in surveys"
                      :label="survey.title"
                      :value="survey.id"
                      :key="survey.id"
                    ></el-option>
                  </el-select>
              </div>
            </div>

            <div class="row" v-if="filterData.survey_id">
              <div class="col-md-12">
                <div style="padding-top: 10px">
                  <i v-if="stageCountLoading.survey" class="pull-right fa fa-spinner fa-spin"></i>
                  <span class="pull-right respondent-count" v-else>Respondents: {{ stageCount.survey.toLocaleString() }}</span>
                </div>
              </div>
              <div class="clearfix"></div>
              <hr />
            </div>

            <div v-if="filterData.survey_id">
              <div class="row">
                <div class="col-md-6">
                  <h3>Demographics</h3>
                  <div v-for="question in demographicQuestions" :key="question.id">
                    <el-form-item :label="question.title">
                      <el-checkbox-group v-if="question.choiceLinks" v-model="selectedDemographics">
                        <el-checkbox
                          v-for="choice in questionChoices(question.id)"
                          :label="`${question.id}:${choice.choice.id}`"
                          :key="choice.id"
                        >{{ choice.choice.title }} </el-checkbox>
                      </el-checkbox-group>
                    </el-form-item>
                  </div>

                  <empty
                    v-if="demographicQuestions.length === 0"
                  >No demographics questions were asked on this survey.</empty>
                </div>

                <div class="col-md-6">
                  <h3>Contact details</h3>
                  <el-checkbox-group v-model="fields">
                    <el-checkbox :label="'Mobile number'" :key="'Mobile number'">Mobile number</el-checkbox>
                    <el-checkbox :label="'All'" :key="'All'">All</el-checkbox>
                    <el-checkbox
                      v-for="question in contactQuestions"
                      :label="question"
                      :key="question"
                    >{{ question }}</el-checkbox>
                  </el-checkbox-group>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div>
                  <i v-if="stageCountLoading.demographics" class="pull-right fa fa-spinner fa-spin"></i>
                  <span class="pull-right respondent-count" v-else>Respondents: {{ stageCount.demographics.toLocaleString() }}</span>
                  </div>
                </div>
                <div class="clearfix"></div>
                <hr />
              </div>

              <div class="row">
                <div class="col-md-12">
                  <h3>Questions</h3>
                  <div
                    class="question"
                    v-for="(selectedQuestion, index) in filterData.questions"
                    :key="index"
                  >
                    <el-form-item label="Question">
                      <el-select
                        placeholder="Please select a question"
                        v-model="selectedQuestion.question_id"
                        @change="handleQuestionsChanged()"
                      >
                        <el-option
                          v-for="question in questions"
                          :label="question.title"
                          :value="question.id"
                          :key="question.id"
                          :disabled="isQuestionUsed(question.id)"
                        ></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item
                      label="Answers"
                      v-if="questionChoices(selectedQuestion.question_id).length > 0"
                    >
                      <el-checkbox-group
                        v-model="selectedQuestion.choices"
                        @change="handleQuestionsChanged()"
                      >
                        <el-checkbox
                          v-for="choice in questionChoices(selectedQuestion.question_id)"
                          :label="choice.choice.id"
                          :value="choice.choice.id"
                          :key="choice.choice.id"
                        >{{ choice.choice.title }}</el-checkbox>
                      </el-checkbox-group>
                    </el-form-item>

                    <div class="question-remove pull-right">
                      <el-button
                        type="text"
                        size="mini"
                        @click="handleRemoveQuestion(index)"
                      >[Remove]</el-button>
                    </div>

                    <div class="clearfix"></div>

                    <div
                      class="question-condition"
                      v-if="
                      filterData.questions.length > 1 && index !== filterData.questions.length - 1
                    "
                    >AND they must have answered...</div>
                  </div>

                  <el-form-item>
                    <el-tooltip class="item" effect="dark" content="Add another question from this survey" placement="bottom">
                    <el-button
                      type="success"
                      round
                      @click="handleAddQuestion()"
                      v-if="unusedQuestions.length > 0"
                      circle
                      icon="el-icon-plus"
                    >
                    </el-button>
                     </el-tooltip>
                  </el-form-item>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div>
                  <i v-if="stageCountLoading.questions" class="pull-right fa fa-spinner fa-spin"></i>
                  <span class="pull-right respondent-count" v-else>Respondents: {{ stageCount.questions.toLocaleString() }}</span>
                  </div>
                </div>
                <div class="clearfix"></div>
                <hr />
              </div>
            </div>

            <div class="row">
              <div class="col-md-12">
                <el-button
                  split-button
                  type="primary"
                  :disabled="generatingList || isGenerateDisabled"
                  v-if="this.filterData.survey_id"
                  @click="handleGenerate()"
                  @command="handleGenerateCommand"
                >
                <i v-if="generatingList" class="fa fa-spinner fa-spin"></i>
                <span else>Generate List</span>

                  <el-dropdown-menu slot="dropdown" v-show="false">
                    <el-dropdown-item command="constant_contact">Constant Contact</el-dropdown-item>
                    <el-dropdown-item command="mail_chimp">Mail Chimp</el-dropdown-item>
                  </el-dropdown-menu>
                </el-button>
                <div class="pull-right">
                  <el-button-group>
                    <el-button type="default" @click="loadVisible = true">
                      <i class="fa fa-fw fa-folder-open-o"></i> Load
                    </el-button>
                    <el-button
                      type="default"
                      @click="saveVisible = true"
                      v-if="this.filterData.survey_id"
                    >
                      <i class="fa fa-fw fa-floppy-o"></i> Save
                    </el-button>
                  </el-button-group>
                </div>
              </div>
            </div>
          </el-form>
        </div>
      </i-box>
    </div>

    <survey-filter-save-dialog
      :is-visible="saveVisible"
      @onVisibleChanged="saveVisible = $event"
      @onSave="handleSave"
    />
    <survey-filter-load-dialog
      :is-visible="loadVisible"
      @onVisibleChanged="loadVisible = $event"
      @onLoad="handleLoad"
    />

    <constant-contact-dialog
      v-if="constantContactVisible"
      :contacts="constantContactList"
      :is-visible="constantContactVisible"
      :loadingContacts="loadingSurveyRespondents"
      v-on:open="getSurveyRespondents"
      @onVisibleChanged="constantContactVisible = $event"
    />

    <mailchimp-dialog
      v-if="mailChimpVisible"
      :contacts="mailchimpContactList"
      :is-visible="mailChimpVisible"
      :loadingContacts="loadingSurveyRespondents"
      v-on:open="getSurveyRespondents"
      @onVisibleChanged="mailChimpVisible = $event"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag';
import moment from 'moment';
import { pick } from 'lodash';
import { ExportToCsv } from 'export-to-csv';

import Empty from '@/components/common/Empty';
import IBox from '@/components/common/IBox';
import Loader from '@/components/common/Loader';

import ConstantContactDialog from '@/components/email/ConstantContactDialog';
import MailchimpDialog from '@/components/email/MailChimpDialog';
import SurveyFilterSaveDialog from '@/components/surveys/SurveyFilterSaveDialog';
import SurveyFilterLoadDialog from '@/components/surveys/SurveyFilterLoadDialog';

export default {
  name: 'SurveyFilter',
  components: {
    Empty,
    IBox,
    Loader,
    ConstantContactDialog,
    MailchimpDialog,
    SurveyFilterSaveDialog,
    SurveyFilterLoadDialog,
  },
  apollo: {
    $client: 'apiv2',
    surveysListAllSurvey: {
      query: gql`
        {
          surveysListAllSurvey {
            entries {
              id
              title
            }
          }
        }
      `,
    },
  },
  data: () => ({
    surveysListAllSurvey: { entries: [] },
    lists: [],
    contacts: [],
    selectedDemographics: [],
    stageCount: {
      survey: 0,
      demographics: 0,
      questions: 0,
    },
    stageCountLoading: {
      survey: false,
      demographics: false,
      questions: false,
    },
    filterData: {
      survey_id: null,
      demographics: [],
      questions: [],
    },
    error: null,
    generatingList: false,
    fields: ['Mobile number'],
    constantContactVisible: false,
    mailChimpVisible: false,
    saveVisible: false,
    loadVisible: false,
    savedId: null,
    surveyRespondents: [],
    loadingSurveyRespondents: false,
    loadingSurvey: false,
  }),
  computed: {
    surveys() {
      return this.surveysListAllSurvey.entries;
    },

    surveyQuestions() {
      if (!this.filterData.survey_id) {
        return [];
      }
      const survey = this.surveys.find(s => s.id === this.filterData.survey_id);

      return survey.questionLinks.map(l => l.question);
    },

    selectableSurveyQuestions() {
      const selectableTypes = ['SELECT', 'SCALE', 'MULTI_SELECT', 'RADIOGROUP'];
      return this.surveyQuestions.filter(q => selectableTypes.includes(q.questionType));
    },

    contactQuestions() {
      const questions = this.surveyQuestions.filter(q => q.isContact).map(q => q.title);
      return [...new Set(questions)];
    },

    demographicQuestions() {
      return this.selectableSurveyQuestions.filter(q => q.isDemographic);
    },

    questions() {
      return this.selectableSurveyQuestions.filter(q => !q.isDemographic && !q.isContact);
    },

    unusedQuestions() {
      const usedQuestionIds = this.filterData.questions.map(q => q.question_id);
      return this.questions.filter(q => !usedQuestionIds.includes(q.id));
    },

    isQuestionUsed() {
      return questionId => {
        const usedQuestionIds = this.filterData.questions.map(q => q.question_id);
        return usedQuestionIds.includes(questionId);
      };
    },

    questionChoices() {
      return questionId => {
        const question = this.surveyQuestions.find(q => q.id === questionId);
        if (!question || !question.choiceLinks) {
          return [];
        }

        return [...question.choiceLinks].sort((a, b) => a.order - b.order);
      };
    },

    isGenerateDisabled() {
      return this.stageCount.questions === 0 || this.fields.length === 0;
    },

    constantContactList() {
      let contactList = [];
      for (let a = 0; a < this.surveyRespondents.length; a += 1) {
        let data = {};
        const hasEmail = this.hasEmailField(this.surveyRespondents[a]);
        if (hasEmail) {
          for (let i = 0; i < this.surveyRespondents[a].length; i += 1) {
            switch (this.surveyRespondents[a][i].profile_field) {
              case 'email':
                data = { email_addresses: [this.surveyRespondents[a][i].input], ...data };
                break;
              case 'name': {
                const { firstName, lastName } = this.getRespondentName(
                  this.surveyRespondents[a][i].input,
                );
                data = {
                  first_name: firstName,
                  last_name: lastName,
                  home_phone: this.surveyRespondents[a][i].mobile_number,
                  ...data,
                };
                break;
              }
              default:
                break;
            }
          }
          contactList = [...contactList, data];
        }
      }
      return contactList;
    },
    mailchimpContactList() {
      let contactList = [];
      for (let a = 0; a < this.surveyRespondents.length; a += 1) {
        let data = {};
        const hasEmail = this.hasEmailField(this.surveyRespondents[a]);
        if (hasEmail) {
          for (let i = 0; i < this.surveyRespondents[a].length; i += 1) {
            switch (this.surveyRespondents[a][i].profile_field) {
              case 'email':
                data = { email_address: this.surveyRespondents[a][i].input, ...data };
                break;
              case 'name': {
                const { firstName, lastName } = this.getRespondentName(
                  this.surveyRespondents[a][i].input,
                );
                data = {
                  status: 'subscribed',
                  merge_fields: {
                    FNAME: firstName,
                    LNAME: lastName,
                    PHONE: this.surveyRespondents[a][i].mobile_number,
                  },
                  ...data,
                };
                break;
              }
              default:
                break;
            }
          }
          contactList = [...this.contactList, data];
        }
      }
      return contactList;
    },
  },

  methods: {
    hasEmailField(responses) {
      return responses.some(res => res.profile_field === 'email');
    },

    async fetchSurvey() {
      this.loadingSurvey = true;
      try {
        const res = await this.$apolloProvider.clients.apiv2.query({
          query: gql`
            query($surveyId: Int!) {
              surveysGetSurvey(surveyId: $surveyId) {
                id
                title
                questionLinks {
                  order
                  question {
                    id
                    title
                    isContact
                    isDemographic
                    profileField
                    questionType
                    choiceLinks {
                      order
                      polarity
                      choice {
                        id
                        title
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            surveyId: parseInt(this.filterData.survey_id, 10),
          },
        });
        const survey = res.data.surveysGetSurvey;
        this.surveysListAllSurvey.entries[this.surveysListAllSurvey.entries.findIndex(
          el => el.id === survey.id)] = survey;
        this.loadingSurvey = false;
        this.handleCountStage('survey');
        this.handleCountStage('demographics');
        this.handleCountStage('questions');
      } catch (error) {
        this.loadingSurvey = false;
        this.error = error;
      }
    },

    getRespondentName(name) {
      const splitName = name.split(' ');
      let firstName = '';
      let lastName = '';
      if (splitName.length > 2) {
        firstName = splitName[0];
        lastName = splitName[2];
        return { firstName, lastName };
      }
      firstName = splitName[0];
      lastName = splitName[1];
      return { firstName, lastName };
    },

    getSurveyRespondents() {
      this.loadingSurveyRespondents = true;
      this.$apollo
        .query({
          query: gql`
            query($filter: Json!, $fields: [String]!) {
              surveyRespondentFilterMap(dynamicFilter: $filter, fields: $fields)
            }
          `,
          variables: {
            filter: JSON.stringify(this.filterData),
            fields: this.fields,
          },
        })
        .then(result => {
          this.surveyRespondents = result.data.surveyRespondentFilterMap;
          this.loadingSurveyRespondents = false;
        })
        .catch(() => {
          this.loadingSurveyRespondents = false;
        });
    },

    handleSurveyChanged() {
      this.fetchSurvey();
      this.resetFilterData();
      // this.getSurveyRespondents();
    },

    buildImportList() {
      for (let a = 0; a < this.dataRespondents.length; a += 1) {
        let data = {};
        const hasEmail = this.hasEmailField(this.dataRespondents[a]);
        if (hasEmail) {
          for (let i = 0; i < this.dataRespondents[a].length; i += 1) {
            switch (this.dataRespondents[a][i].profile_field) {
              case 'email':
                data = { email_address: this.dataRespondents[a][i].input, ...data };
                break;
              case 'name': {
                const { firstName, lastName } = this.getRespondentName(
                  this.dataRespondents[a][i].input,
                );
                data = {
                  status: 'subscribed',
                  merge_fields: {
                    FNAME: firstName,
                    LNAME: lastName,
                    PHONE: this.dataRespondents[a][i].mobile_number,
                  },
                  ...data,
                };
                break;
              }
              default:
                break;
            }
          }
          Object.keys(this.selectedLists).forEach(key => {
            let operations = [];
            const listIds = this.selectedLists[key];
            listIds.forEach(listId => {
              operations = [
                ...operations,
                {
                  method: 'POST',
                  path: `/lists/${listId}/members/`,
                  operation_id: `add-${data.email_address}-${listId}`,
                  body: data,
                },
              ];
              this.operations = {
                ...this.operations,
                [key]: [...operations],
              };
            });
          });
        }
      }
    },

    handleDemographicsChanged() {
      this.handleCountStage('demographics');
      this.handleCountStage('questions');
    },

    handleQuestionsChanged() {
      this.handleCountStage('questions');
    },

    handleAddQuestion() {
      this.filterData.questions.push({ question_id: null, choices: [] });
    },

    handleRemoveQuestion(index) {
      this.filterData.questions.splice(index, 1);
      this.handleQuestionsChanged();
    },

    handleCountStage(stage) {
      const stageFields = {
        survey: ['survey_id'],
        demographics: ['survey_id', 'demographics'],
        questions: ['survey_id', 'demographics', 'questions', 'condition'],
      };
      const stageFilter = pick(this.filterData, stageFields[stage]);
      this.stageCountLoading[stage] = true;

      this.$apollo
        .query({
          query: gql`
            query($dynamicFilter: Json!) {
              surveysFilterRespondentsCount(dynamicFilter: $dynamicFilter)
            }
          `,
          variables: { dynamicFilter: JSON.stringify(stageFilter) },
        })
        .then(result => {
          const count = result.data.surveysFilterRespondentsCount;
          this.stageCount[stage] = count;
          this.stageCountLoading[stage] = false;
          return count;
        });
    },

    handleGenerate() {
      if (this.fields.includes('All')) this.fields = ['All'];
      this.generatingList = true;
      this.$apollo
        .query({
          query: gql`
            query($filter: Json!, $fields: [String]!) {
              surveysFilterRespondents(dynamicFilter: $filter, fields: $fields)
            }
          `,
          variables: { filter: JSON.stringify(this.filterData), fields: this.fields },
        })
        .then(result => {
          this.createCsv(result.data.surveysFilterRespondents, this.fields);
          this.generatingList = false;
        });
    },

    formatToCSV(rows) {
      return rows.map(row => row.map(value => value.answer));
    },
    handleGenerateCommand(cmd) {
      switch (cmd) {
        case 'constant_contact':
          this.constantContactVisible = true;
          break;
        case 'mail_chimp':
          this.mailChimpVisible = true;
          break;
        default:
          break;
      }
    },

    handleSave(title) {
      this.saveVisible = false;
      this.$apollo
        .mutate({
          client: 'apiv2',
          mutation: gql`
            mutation($savedFilter: SurveysSavedFilterInput!) {
              surveyFilterCreateSaved(savedFilter: $savedFilter) {
                id
              }
            }
          `,
          variables: {
            savedFilter: {
              title,
              filter: JSON.stringify(this.filterData),
              fields: this.fields,
              surveyId: this.filterData.survey_id,
            },
          },
        })
        .then(() => {
          this.$notify({
            title: 'Success',
            message: 'Saved survey filter successfully.',
            type: 'success',
          });
          this.saveVisible = false;
        });
    },

    handleLoad([id, filterData, fields]) {
      this.savedId = id;
      this.filterData = filterData;
      this.fields = fields;
      this.selectedDemographics = filterData.demographics.flatMap(d =>
        d.choices.map(c => `${d.question_id}:${c}`),
      );
      this.loadVisible = false;

      // selected demographic watcher will update the demographics and question
      // stages, the only one that won't update is the survey stage, lets to that now.
      this.handleCountStage('survey');
    },

    resetFilterData() {
      this.selectedDemographics = [];
      this.filterData.demographics = [];
      this.filterData.questions = [];
      this.fields = ['Mobile number'];
    },

    createCsv(data) {
      const headers = this.getCsvHeaders(data);
      const rows = this.getCsvRows(data, headers);
      const csvExporter = new ExportToCsv({
        headers: this.escapeCommas(headers),
        showLabels: true,
        filename: `survey-filter-${moment().format('YYYYMMDDHHmmss')}`,
      });
      csvExporter.generateCsv(rows);
    },

    getCsvRows(data, headers) {
      let rows = [];
      data.forEach(responses => {
        const missingRows = this.getCsvMissingResponseRows(responses, headers);
        let row = [];
        responses.forEach((response, i) => {
          const missing = missingRows[i];
          if (missing) {
            row = [...row, 'N/A'];
          }
          row = [...row, response.answer];
        });
        rows = [...rows, row];
      });
      return rows;
    },

    getCsvHeaders(data) {
      let headers = [];
      data.forEach(responses => {
        responses.forEach(response => {
          const found = headers.find(h => h === response.question);
          if (!found) {
            headers = [...headers, response.question];
          }
        });
      });
      return headers;
    },

    getCsvMissingResponseRows(responses, headers) {
      let missing = {};
      let missingIndex = 0;
      headers.forEach((h, i) => {
        const found = responses.find(r => {
          if (h === r.question) return true;
          missingIndex = i;
          return false;
        });
        if (!found) {
          missing = { ...missing, [missingIndex]: h };
        }
      });
      return missing;
    },

    escapeCommas(headers) {
      return headers.map(h => `"${h}"`);
    },
  },
  watch: {
    selectedDemographics(value) {
      const extractValues = v => {
        const [questionId, choiceId] = v.split(':');
        return { questionId, choiceId };
      };

      this.filterData.demographics = value.reduce((acc, v) => {
        const { questionId, choiceId } = extractValues(v);
        const demographic = acc.find(a => a.question_id === Number(questionId));

        if (!demographic) {
          return [...acc, { question_id: Number(questionId), choices: [Number(choiceId)] }];
        }

        demographic.choices.push(choiceId);

        return acc;
      }, []);
      this.handleDemographicsChanged();
    },
  },
};
</script>

<style lang="scss" scoped>
div >>> .el-checkbox {
  display: block;
  line-height: 1px;
  margin-bottom: 0;
}

div >>> .el-select {
  width: 100%;
}

.respondent-count {
  font-size: 1.2em;
  border-bottom: 2px solid #000;
}

.question-condition {
  font-style: oblique;
  font-weight: bold;
  font-size: 1.1em;
  margin-bottom: 20px;
}
</style>



// WEBPACK FOOTER //
// SurveyFilter.vue?3bfa1e12